import * as React from "react";
import { toast } from "sonner";
import { ICompasAIChatAction } from "../../types/compass.types";
import { useCompassSocketContext } from "@/context/CompassSocketContext";
import CreateRequestDialog from "./request-gallery/CreateRequestDialog";
import { useActionItemHandlers } from "./action-item/action-item.utils";
import { ActionItemCardBody } from "./action-item/ActionItemCardBody";

interface ActionItemProps {
  action: ICompasAIChatAction;
  isExpanded: boolean;
  toggleExpansion: () => void;
  actionState: string;
  isGlobalEditMode: boolean;
}

const useActionItemState = (initialPrompt: string, isGlobalEditMode: boolean) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedPrompt, setEditedPrompt] = React.useState(initialPrompt);
  const [isCreateRequestOpen, setIsCreateRequestOpen] = React.useState(false);
  const [createRequestData, setCreateRequestData] = React.useState<
    { name: string; content: string } | undefined
  >(undefined);

  React.useEffect(() => {
    setIsEditing(isGlobalEditMode);
  }, [isGlobalEditMode]);

  return {
    isEditing,
    setIsEditing,
    editedPrompt,
    setEditedPrompt,
    isCreateRequestOpen,
    setIsCreateRequestOpen,
    createRequestData,
    setCreateRequestData,
  };
};

const getCardClickAction = (
  isEditing: boolean,
  currentStatus: string,
  cancelAction: () => void,
  executeAction: () => void
) => {
  if (isEditing) return;
  if (currentStatus === "queued" || currentStatus === "pending") {
    cancelAction();
    return;
  }
  if (currentStatus === "cancelled") {
    toast.info("This action was cancelled", { duration: 2000 });
    return;
  }
  if (
    currentStatus === "normal" ||
    currentStatus === "processed" ||
    currentStatus === "completed"
  ) {
    executeAction();
  }
};

const ActionItemCard: React.FC<ActionItemProps> = ({
  action,
  isExpanded,
  toggleExpansion,
  actionState,
  isGlobalEditMode,
}) => {
  const { taskStatuses } = useCompassSocketContext();
  const statusKey = action.queue_item_id ?? action.id;
  const currentStatus = taskStatuses[statusKey] ?? actionState;

  const {
    isEditing,
    setIsEditing,
    editedPrompt,
    setEditedPrompt,
    isCreateRequestOpen,
    setIsCreateRequestOpen,
    createRequestData,
    setCreateRequestData,
  } = useActionItemState(action.prompt, isGlobalEditMode);

  const {
    handleToggleEditing,
    handleDeleteAction,
    cancelAction,
    executeAction,
    handleCancelEditing,
    editActionMutation,
  } = useActionItemHandlers(action, editedPrompt, setIsEditing, setEditedPrompt);

  const onToggleEditing = () => {
    if (isEditing) {
      handleToggleEditing();
    } else {
      setIsEditing(true);
    }
  };

  const handleSaveToLibrary = () => {
    setCreateRequestData({ name: action.heading || action.label || "", content: action.prompt });
    setIsCreateRequestOpen(true);
  };

  const handleCardClick = () => {
    getCardClickAction(isEditing, currentStatus, cancelAction, executeAction);
  };

  return (
    <>
      <ActionItemCardBody
        action={action}
        isExpanded={isExpanded}
        toggleExpansion={toggleExpansion}
        isEditing={isEditing}
        editedPrompt={editedPrompt}
        setEditedPrompt={setEditedPrompt}
        handleCancelEditing={handleCancelEditing}
        onToggleEditing={onToggleEditing}
        handleDeleteAction={handleDeleteAction}
        handleSaveToLibrary={handleSaveToLibrary}
        isPending={editActionMutation.isPending}
        currentStatus={currentStatus}
        handleCardClick={handleCardClick}
      />
      <CreateRequestDialog
        open={isCreateRequestOpen}
        onOpenChange={setIsCreateRequestOpen}
        initialData={createRequestData}
        type="discuss"
      />
    </>
  );
};

const ActionItem: React.FC<ActionItemProps> = (props) => <ActionItemCard {...props} />;

export default ActionItem;
